<script setup lang="ts">
import type { SectionName } from '~/types/mixpanel/index.js';

import { defaultTwitterCardMeta } from '@/constants/seo';
import { createPopup as typeformCreatePopup } from '@typeform/embed';
import { useEvents } from '~/composables/events/useEvents';
import { useAbsoluteUrl } from '~/composables/useAbsoluteUrl';
import { MixpanelProps } from '~/core/service/mixpanel/index.js';
import { HERO_LAYOUT } from '~~/utils/symbols.js';

import { Breadcrumb, BreadcrumbItem } from '~/components/ui/breadcrumb';

import OffMarketPageData from '../../data/pages/offMarket.js';

// TODO: Escrever teste e2e para está página com cypress
const strings = OffMarketPageData.pt;

definePageMeta({
	layout: 'default-transparent',
	path: '/off-market'
});

const {
	trackPageView,
	trackFormOffMarketClicked,
	trackFaqClicked,
	trackBreadcrumbClicked
} = useEvents();

const route = useRoute();
const { user } = useUserSession();

const hero = inject(HERO_LAYOUT, null);

const benefitsSection = computed(() => strings.benefits);
const buySection = computed(() => strings.buy);
const sellSection = computed(() => strings.sell);
const questions = computed(() => strings.questions);

const isOutHero = shallowRef<boolean>(false);
const { y } = useScroll(window);

const breadcrumbItems = [
	{ label: strings.breadcrumb.home, route: '/' },
	{
		label: strings.breadcrumb.offMarket,
		route: '/off-market'
	}
];

watch(y, scrollPosition => {
	const heroHeight = window.innerHeight * 0.9 - 80;

	if (!hero) return;

	if (scrollPosition > heroHeight && !isOutHero.value) {
		hero.headerVariant.value = 'primary';
		isOutHero.value = true;
	}

	if (scrollPosition < heroHeight && isOutHero.value) {
		hero.headerVariant.value = 'translucide';
		isOutHero.value = false;
	}
});

const typeformPopup = ref<ReturnType<typeof typeformCreatePopup>>();

const { offMarketTypeformId } = useRuntimeConfig().public;
onMounted(() => {
	typeformPopup.value = typeformCreatePopup(offMarketTypeformId);

	trackPageView({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.PREVIOUS_PAGE_URL]: document.referrer
	});
});

const handleOpenTypeform = (section: SectionName) => {
	typeformPopup.value?.open();

	trackFormOffMarketClicked({
		[MixpanelProps.CTA_TEXT]: strings.CTA.talkToSpecialist.label,
		[MixpanelProps.USER_NAME]: user.value?.name || undefined,
		[MixpanelProps.USER_EMAIL]: user.value?.email || undefined,
		[MixpanelProps.USER_PHONE]: user.value?.phoneNumber || undefined,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: section
	});
};

const { toAbsoluteUrl } = useAbsoluteUrl();
const ogImageUrl = toAbsoluteUrl('/images/social/openGraph/og_offMarket.jpg');

useSeoMeta({
	title: 'Comprar e vender imóveis Off-Market | PilarHomes',
	description:
		'Uma nova forma de comprar e vender com discrição. Com Off-Market, você garante privacidade total, negociações reservadas e acesso a imóveis exclusivos.',
	keywords: 'comprar, vender, off-market',
	ogTitle: 'Comprar e vender imóveis Off-Market | PilarHomes',
	ogDescription:
		'Uma nova forma de buscar e vender com discrição. Com Off-Market, você garante privacidade total, negociações reservadas e acesso a imóveis exclusivos.',
	ogImage: ogImageUrl,
	twitterCard: defaultTwitterCardMeta,
	twitterTitle: 'Comprar e vender imóveis Off-Market | PilarHomes',
	twitterDescription:
		'Uma nova forma de buscar e vender com discrição. Com Off-Market, você garante privacidade total, negociações reservadas e acesso a imóveis exclusivos.',
	twitterImage: ogImageUrl
});

const handleFaqClick = (question: string, position: number) => {
	if (question) {
		trackFaqClicked({
			[MixpanelProps.QUESTION_TEXT]: question,
			[MixpanelProps.CURRENT_PAGE]: route.fullPath,
			[MixpanelProps.QUESTION_POSITION]: position
		});
	}
};

function handleBreadcrumbClicked(item: BreadcrumbItem) {
	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: item.label,
		[MixpanelProps.DESTINATION_URL]: String(item.route),
		[MixpanelProps.CLICK_HOME]: item.route === '/'
	});
}
</script>

<template>
	<div class="relative h-[90dvh] w-full">
		<NuxtImg
			:src="'/images/offMarket/hero.webp'"
			preload
			sizes="400px md:100vw"
			format="webp"
			loading="lazy"
			class="absolute inset-0 size-full object-cover object-left md:object-center"
			fetchpriority="high"
		/>
		<div class="absolute inset-0 bg-primary/38"></div>
		<Container
			class="absolute z-20 flex size-full"
			main-class="flex flex-col gap-5 md:gap-0 md:flex-row justify-end md:items-end md:justify-between"
		>
			<div class="flex flex-col gap-5 md:w-3/5">
				<h1
					class="whitespace-nowrap font-matter-sq text-[32px] font-normal leading-[110%] tracking-[-0.96px] text-white md:text-5xl"
				>
					{{ strings.hero.title }}
				</h1>
				<h2 class="text-base font-normal leading-normal text-white md:text-xl">
					{{ strings.hero.description }}
				</h2>
			</div>
			<Button
				class="size-min px-6 py-3 md:text-lg"
				@click="handleOpenTypeform('hero section')"
			>
				{{ strings.CTA.talkToSpecialist.label }}

				<Component :is="strings.CTA.talkToSpecialist.icon" class="size-6" />
			</Button>
		</Container>
	</div>

	<Container class="block h-[10vh] bg-white pb-0! pt-4 md:px-32! md:pt-6">
		<Breadcrumb
			:items="breadcrumbItems"
			@breadcrumb-clicked="handleBreadcrumbClicked"
		/>
	</Container>

	<Container
		class="bg-white pb-0 pt-10 md:pb-11 md:pt-15"
		main-class="flex flex-col gap-8 md:flex-row"
	>
		<div
			v-for="benefit in benefitsSection"
			:key="`benefit: ${benefit.title}`"
			class="flex w-full gap-6 md:flex-col md:gap-1"
		>
			<div
				class="before:trapeze relative z-10 h-12 pb-2 text-red-500 before:top-[12px] before:z-0 before:h-6 before:w-[60px] before:bg-secondary-dark-1 md:w-full"
			>
				<Component
					:is="benefit.icon"
					class="sticky z-10 size-12 text-primary"
				/>
			</div>
			<div>
				<p
					class="font-matter-sq text-xl font-normal leading-normal tracking-[-0.4px] text-primary"
				>
					{{ benefit.title }}
				</p>

				<p class="text-sm font-normal leading-normal text-primary-light-3">
					{{ benefit.description }}
				</p>
			</div>
		</div>
	</Container>

	<Container
		class="flex items-center bg-white md:pb-[60px] md:pt-10"
		main-class="flex flex-col md:flex-row items-center md:h-[380px] lg:h-[320px]"
	>
		<Card
			class="relative order-2 flex size-full h-full flex-col bg-secondary md:order-1 md:w-1/2"
		>
			<CardHeader class="px-6 pt-6 md:py-9 md:pr-9">
				<CardTitle
					class="text-2xl tracking-[-0.48px] text-primary md:text-[28px] md:tracking-[-0.56px]"
				>
					{{ strings.whatAreOffMarketProperties.card.title }}
				</CardTitle>
				<CardDescription class="hidden"></CardDescription>
				<div class="absolute right-9 size-5">
					<!-- <GraphicsTriangle class="text-white" /> -->
				</div>
			</CardHeader>
			<CardContent
				class="px-6 text-sm font-normal leading-loose text-primary-light-3 md:text-lg"
			>
				<p>
					{{ strings.whatAreOffMarketProperties.card.content }}
				</p>
			</CardContent>
			<CardFooter class="mt-auto h-auto px-6 pb-6">
				<div class="w-[320px]">
					<Button
						class="size-min px-6 py-3 text-sm md:text-lg"
						@click="handleOpenTypeform('page content')"
					>
						{{ strings.CTA.talkToSpecialist.label }}

						<Component :is="strings.CTA.talkToSpecialist.icon" class="size-6" />
					</Button>
				</div>
			</CardFooter>
		</Card>
		<div
			class="relative order-1 size-full min-h-[320px] grow md:order-2 md:w-1/2"
		>
			<NuxtImg
				src="/images/offMarket/off-market-1.webp"
				format="webp"
				class="absolute inset-0 size-full object-cover object-[30%_50%] sm:object-[40%_65%] lg:object-[50%_65%]"
			/>
		</div>
	</Container>

	<Container class="flex items-center bg-white md:border-none md:pb-[80px]">
		<h4
			class="pb-8 font-matter-sq text-[28px] font-normal leading-normal tracking-[-0.56px] text-primary md:text-4xl md:tracking-[-0.72px]"
		>
			{{ strings.whoCanBenefitWithOffMarketProperties.title }}
		</h4>
		<div class="flex flex-col items-center md:flex-row">
			<div class="relative h-[320px] w-full max-w-screen-sm-large md:w-1/2">
				<NuxtImg
					src="/images/offMarket/off-market-2.webp"
					format="webp"
					class="size-full object-cover object-[30%_50%] lg:object-[50%_55%]"
					sizes="500px xl:720px"
				/>
			</div>
			<div class="w-full space-y-5 pt-6 md:w-1/2 md:space-y-8 md:pl-11 md:pt-0">
				<div
					v-for="item in strings.whoCanBenefitWithOffMarketProperties.items"
					:key="`who-can-benefit-item: ${item}`"
					class="flex items-center gap-4"
				>
					<span class="grid size-8 min-w-8 place-items-center bg-white-dark-2">
						<Component
							:is="strings.whoCanBenefitWithOffMarketProperties.itemsIcon"
							class="size-6 text-primary"
						/>
					</span>
					<p
						class="font-matter-sq text-lg font-normal leading-normal tracking-[-0.4px] text-primary md:text-xl"
					>
						{{ item }}
					</p>
				</div>
			</div>
		</div>
	</Container>

	<Container class="bg-primary" main-class="space-y-8 md:space-y-11">
		<div class="flex items-start gap-4 text-white md:items-center">
			<Component
				:is="buySection.icon"
				class="mt-[6px] size-8 min-h-8 min-w-8 md:mt-0 md:size-10"
			/>
			<p
				class="font-matter-sq text-[28px] font-normal leading-normal tracking-[-0.72px] md:text-4xl"
			>
				{{ buySection.title }}
			</p>
		</div>
		<div class="flex flex-col md:flex-row md:py-5">
			<div
				v-for="(step, index) in buySection.steps"
				:key="`buy-section-step: ${step.title}`"
				class="flex gap-4 md:flex-col md:gap-1"
			>
				<div
					v-if="index !== buySection.steps.length - 1"
					class="relative md:mb-2 md:pt-0"
				>
					<span
						class="absolute left-1/2 block h-full w-[2px] bg-primary-light-3 md:left-auto md:top-1/2 md:h-[2px] md:w-full md:-translate-y-1/2"
					/>
					<p
						class="sticky z-10 grid size-8 place-items-center bg-beige text-lg font-normal leading-normal text-primary outline-8 outline-primary md:size-10"
					>
						{{ index + 1 }}
					</p>
				</div>
				<div v-else class="relative mb-2 md:pt-0">
					<span
						class="sticky z-10 grid size-8 place-items-center bg-success-light-2 text-lg font-normal leading-normal text-primary outline-8 outline-primary md:size-10"
					>
						<Component :is="strings.buy.lastStepIcon" class="size-6" />
					</span>
				</div>
				<div
					class="mt-[-6px] space-y-1 text-white md:mt-0 md:space-y-0 md:pb-0 md:pr-8"
					:class="{ 'pb-9': index !== sellSection.steps.length - 1 }"
				>
					<p
						class="font-matter-sq text-lg font-normal leading-normal tracking-[-0.36px] md:text-xl md:tracking-[-0.4px]"
					>
						{{ step.title }}
					</p>
					<p class="text-sm font-normal leading-normal">
						{{ step.description }}
					</p>
				</div>
			</div>
		</div>
	</Container>

	<Container
		class="border-t-2 border-primary-light-4 bg-primary"
		main-class="space-y-8 md:space-y-11"
	>
		<div class="flex items-start gap-4 text-white md:items-center">
			<Component
				:is="sellSection.icon"
				class="mt-[6px] size-8 min-h-8 min-w-8 md:mt-0 md:size-10"
			/>
			<p
				class="font-matter-sq text-[28px] font-normal leading-normal tracking-[-0.72px] md:text-4xl"
			>
				{{ sellSection.title }}
			</p>
		</div>
		<div class="flex flex-col md:flex-row md:py-5">
			<div
				v-for="(step, index) in sellSection.steps"
				:key="`buy-section-step: ${step.title}`"
				class="flex gap-4 md:flex-col md:gap-1"
			>
				<div
					v-if="index !== sellSection.steps.length - 1"
					class="relative md:mb-2 md:pt-0"
				>
					<span
						class="absolute left-1/2 block h-full w-[2px] bg-primary-light-3 md:left-auto md:top-1/2 md:h-[2px] md:w-full md:-translate-y-1/2"
					/>
					<p
						class="sticky z-10 grid size-8 place-items-center bg-beige text-lg font-normal leading-normal text-primary outline-8 outline-primary md:size-10"
					>
						{{ index + 1 }}
					</p>
				</div>
				<div v-else class="relative mb-2 md:pt-0">
					<span
						class="sticky z-10 grid size-8 place-items-center bg-success-light-2 text-lg font-normal leading-normal text-primary outline-8 outline-primary md:size-10"
					>
						<Component :is="strings.sell.lastStepIcon" class="size-6" />
					</span>
				</div>
				<div
					class="mt-[-6px] space-y-1 text-white md:mt-0 md:space-y-0 md:pb-0 md:pr-8"
					:class="{ 'pb-9': index !== sellSection.steps.length - 1 }"
				>
					<p
						class="font-matter-sq text-lg font-normal leading-normal tracking-[-0.36px] md:text-xl md:tracking-[-0.4px]"
					>
						{{ step.title }}
					</p>
					<p class="text-sm font-normal leading-normal">
						{{ step.description }}
					</p>
				</div>
			</div>
		</div>
	</Container>

	<Container class="bg-white-dark-3">
		<ContentBlock
			variant="white-dark-3"
			as="div"
			:title="strings.questionsTitle"
			content-class="space-y-8 p-0 max-w-[940px] mx-auto bg-transparent md:p-0"
			title-class="max-w-[940px] mx-auto"
			class="p-0"
		>
			<QuestionsAccordion
				:questions="questions"
				class="bg-white"
				@question-clicked="e => handleFaqClick(e.question, e.position)"
			/>
			<Button
				class="tablet-desktop-only mx-auto size-min px-6 py-3"
				@click="handleOpenTypeform('faq')"
			>
				{{ strings.CTA.talkToSpecialist.label }}

				<Component :is="strings.CTA.talkToSpecialist.icon" class="size-6" />
			</Button>
		</ContentBlock>
	</Container>
</template>
